import omit from "lodash/omit";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

import {
  IgetWithdrawRequestParams,
  IoperationBatchStatus,
  Ipage,
} from "../../../api/types";
import { ChainSingleSelection } from "../../../components/ChainSelection";
import { useDatePicker } from "../../../components/DatePicker";
import { useAssets, useChains } from "../../../components/FetchConfig";
import GeneralBtn from "../../../components/GeneralBtn";
import { MultiSelection } from "../../../components/GeneralSelection";
import LoadingDialog from "../../../components/LoadingDialog";
import MpTextField from "../../../components/MpTextField";
import {
  Box,
  Container,
  InputAdornment,
  SelectChangeEvent,
} from "../../../components/MuiGenerals";
import {
  useAlerting,
  usePermission,
  useTabs,
  useTranslation,
} from "../../../hooks";
import {
  enumNewOperationBatchStatus,
  tableConfig,
} from "../../../utils/constant";
import {
  displayAmount,
  downloadFiles,
  enumMapping,
  findChainInfo,
  getDecimal,
  getFullApiResponse,
  isOperationSmartAccount,
  listMappingTransform,
  separateBatchStrings,
  toDisplayTime,
} from "../../../utils/helper";
import { DivideLine, genField } from "../../../utils/HelperComp";
import { customSx } from "../../../utils/styling";
import { createZusInstance } from "../../../zustand/store";
import WalletDeploymentRequestsList from "./WalletDeploymentRequestsList";
import {
  createWalletDeploymentRequest,
  getWalletDeploymentRequests,
} from "src/api/merchant";
import { FeatureCode } from "@wallet-manager/node-types";
import { AddressBookOrder } from "../../AssetManagement/Swap/types";
import CreateRequestDialog from "./CreateRequestDialog";
import CreateRequestDialogContent from "./CreateRequestDialog/CreateRequestDialogContent";
import { SmartAccountType } from "@wallet-manager/node-types/dist/src/postgres/const";
import { createWalletNameWithPrefix } from "./helpers/createWalletNameWithPrefix";
import {
  useWalletVersionContext,
  WalletVersionProvider,
} from "src/components/Common/WalletVersionSelection/WalletVersionProvider";

const translatePrefix = "walletDeploymentRequests";
const Enum = {
  operationBatchStatus: enumMapping(enumNewOperationBatchStatus),
};

export default function WalletDeploymentRequests() {
  const { tc } = useTranslation(translatePrefix);
  const tabsArr = [{ name: tc("table"), value: "" }];
  const { Tabs } = useTabs(tabsArr);
  return (
    <Box sx={customSx.layoutBox}>
      <WalletVersionProvider>
        <Container disableGutters maxWidth={false}>
          <FilterBar />
        </Container>
        <DivideLine />
        <Container
          style={customSx.datagridContainer}
          maxWidth={false}
          disableGutters
        >
          <Tabs>
            <WalletDeploymentRequestsList listMapping={listMapping} />
          </Tabs>
        </Container>
      </WalletVersionProvider>
    </Box>
  );
}
interface Ifields {
  chain_name: string;
  operationStatuses: IoperationBatchStatus[];
  batchIdsString: string;
  owner_address: string;
  contract_address: string;
}
const initFields = {
  chain_name: "",
  operationStatuses: [] as IoperationBatchStatus[],
  batchIdsString: "",
  owner_address: "",
  contract_address: "",
} as const;

const initZusParams = {
  createdDateFrom: "",
  createdDateTo: "",
  batch_ids: [] as string[],
  merchant_order_ids: [] as string[],
  page: 0,
  pageSize: tableConfig.pageSize,
  chain_id: "",
  chain_type: "",
  ...omit(initFields, ["batchIdsString", "merchantIdsString"]),
};
export interface CreateRequestDialogFields {
  chainName: string;
  addressBookOrder?: AddressBookOrder;
  ownerAddress: string;
  walletName: string;
  isSmartAccount: boolean | null;
  contractVersion: string;
  rawWalletInfo?: {
    id: string;
    merchant_id: string;
    wallet_type: number;
    wallet_address: string;
    chain_type: number;
    chain_id: string;
    status: number;
    wallet_name: string;
    client_id: null;
    is_default: boolean;
    wallet_version: number;
    wallet_path: string;
    wallet_index: string;
    created_by: string;
    created_date: Date;
    last_modified_date: Date;
    data_signature: string;
    data_signature_version: number;
    data_signer_server_type: number;
    data_signer_server_id: string;
    display_name: string;
    save_records: boolean;
    risk_level: number;
    no_risk_level_count: number;
    control_groups: null;
    assign_wallet_time: Date;
    smart_account_type: number;
  };
}

const initCreateRequestDialogFields: CreateRequestDialogFields = {
  chainName: "",
  addressBookOrder: {
    to_address: "",
    amount: "",
    to_wallet_tag: "",
    wallet_type: "",
    display_name: "",
  } as AddressBookOrder,
  ownerAddress: "",
  walletName: "",
  isSmartAccount: null,
  contractVersion: "",
};

export const useZusParams = createZusInstance<
  IgetWithdrawRequestParams & Ipage
>(initZusParams);
function FilterBar() {
  const DateObj = {
    Created: useDatePicker(),
    Modified: useDatePicker(),
  };
  const { hasPermission } = usePermission();
  const [fields, setFields] = useState<Ifields>(initFields);
  const [createRequestDialogFields, setCreateRequestDialogFields] =
    useState<CreateRequestDialogFields>(initCreateRequestDialogFields);
  const [isCreateRequestDialogOpen, setIsCreateRequestDialogOpen] =
    useState(false);
  const [isCreateRequestLoading, setIsCreateRequestLoading] = useState(false);
  const { selectAssetNamesByChain } = useAssets();
  const { selectChainByPieces } = useChains();
  const asset_names = selectAssetNamesByChain({
    chain_name: fields.chain_name,
  });
  const { alerting } = useAlerting();
  const { t, te, tc } = useTranslation(translatePrefix);
  const zusParams = useZusParams();

  const { getOne } = useWalletVersionContext();

  const setAssets = (asset_names: string[]) =>
    setFields((fields) => ({ ...fields, asset_names }));

  useEffect(() => setAssets([]), [fields.chain_name]);

  const onChange =
    (type: keyof Ifields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  function getParams() {
    const { batchIdsString, chain_name, operationStatuses, ...rest } = fields;

    const { chain_id, chain_type } = selectChainByPieces({ chain_name });

    const payload = {
      ...rest,
      batch_ids: separateBatchStrings(fields.batchIdsString),
      //   merchant_order_ids: separateBatchStrings(fields.merchantIdsString),
      chain_id,
      chain_type: String(chain_type || ""),
      statuses: operationStatuses,
      createdDateFrom: DateObj.Created.start,
      createdDateTo: DateObj.Created.end,
      last_modified_date_from: DateObj.Modified.start,
      last_modified_date_to: DateObj.Modified.end,
      page: 0,
    };

    return payload as any;
  }

  const apiParams = getParams();

  const onSearch = () => {
    zusParams.setBody(apiParams);
    zusParams.refetch();
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.Created.clearDate();
    DateObj.Modified.clearDate();
  };

  const listRes = useQuery({
    queryKey: [
      "getWalletDeploymentRequests",
      zusParams.body,
      zusParams.refetchCounter,
    ],
    queryFn: () => getWalletDeploymentRequests(zusParams.body),
  });

  const onExport = async () => {
    const count = listRes.data?.count || 0;

    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }

    const apiFn = (page: number, pageSize: number, signal: any) =>
      getWalletDeploymentRequests({ ...apiParams, page, pageSize }, { signal });

    const rawRes = await getFullApiResponse(apiFn, count);

    if (rawRes.length === 0) return;

    const res = listMapping("name", rawRes, {
      t,
      te,
      omitKeys: ["rawData"],
      getOneWalletVersionInfo: getOne,
    });
    const config = {};
    downloadFiles(`Wallet Deployment Requests `, res, config);
  };

  const onArraySelectChange =
    (key: keyof Ifields) => (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      const newValue = typeof value === "string" ? value.split(",") : value;
      return setFields((fields) => ({ ...fields, [key]: newValue }));
    };
  const onFilterChange =
    (type: keyof Ifields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const onCreateRequestDialogClose = () => {
    setIsCreateRequestDialogOpen(false);
    setCreateRequestDialogFields(initCreateRequestDialogFields);
  };

  const onCreateRequestConfirm = async () => {
    const chainName = createRequestDialogFields.chainName;
    const walletAddress = createRequestDialogFields.ownerAddress;
    const walletName = createWalletNameWithPrefix(
      createRequestDialogFields?.rawWalletInfo?.wallet_name || "",
    );
    const smartAccountType = createRequestDialogFields.isSmartAccount
      ? SmartAccountType.TronRentalEnergyBandwidth
      : SmartAccountType.EOA;

    if (!chainName) {
      return alerting("error", t("select_chain_name"));
    }

    if (!createRequestDialogFields.contractVersion) {
      return alerting("error", t("select_contract_version"));
    }

    if (!walletAddress) {
      return alerting("error", t("input_wallet_address"));
    }

    if (!walletName) {
      return alerting("error", t("plz_enter_wallet_name"));
    }

    const { chain_type, chain_id } = selectChainByPieces({
      chain_name: chainName,
    });

    setIsCreateRequestLoading(true);
    const res = await createWalletDeploymentRequest({
      chain_type: Number(chain_type),
      chain_id: Number(chain_id),
      wallet_address: walletAddress,
      multisign_wallet_name: walletName,
      operation_smart_account_type: smartAccountType,
      contract_version: createRequestDialogFields.contractVersion,
    });
    setIsCreateRequestLoading(false);

    if (!res) return;

    alerting("success", t("create_request_success"));
    onCreateRequestDialogClose();
  };

  const F = genField({ t }, [
    ["created_date", <DateObj.Created.Picker type="dateTime" />],
    ["last_modified_time", <DateObj.Modified.Picker type="dateTime" />],
    [
      "chain_name",
      <ChainSingleSelection
        label={t("ph_chain_name")}
        setChoice={(chain_name) => setFields((f) => ({ ...f, chain_name }))}
        choice={fields.chain_name}
        onlyEthAndTron
      />,
    ],
    // [
    //   "asset_name",
    //   <AssetMultiSelection
    //     label={t("ph_asset_name")}
    //     setChoices={setAssets}
    //     choices={fields.asset_names}
    //     allItems={asset_names}
    //   />,
    // ],
    [
      "status",
      <MultiSelection
        label={t("ph_status")}
        value={fields.operationStatuses}
        onChange={onArraySelectChange("operationStatuses")}
        enumData={enumNewOperationBatchStatus}
      />,
    ],
    [
      "batch_id",
      <MpTextField
        label={t("ph_batch_id")}
        value={fields.batchIdsString}
        onChange={onChange("batchIdsString")}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {apiParams.batch_ids.length}
            </InputAdornment>
          ),
        }}
      />,
    ],
    [
      "owner_address",
      <MpTextField
        label={tc("phInputField", { fieldName: t("owner_address") })}
        value={fields.owner_address}
        onChange={onChange("owner_address")}
      />,
    ],
    [
      "contract_address",
      <MpTextField
        label={tc("phInputField", { fieldName: t("contract_address") })}
        value={fields.contract_address}
        onChange={onChange("contract_address")}
      />,
    ],

    // [
    //   "merchant_order_id",
    //   <MpTextField
    //     label={t("ph_merchant_order_id")}
    //     value={fields.merchantIdsString}
    //     onChange={onFilterChange("merchantIdsString")}
    //     InputProps={{
    //       endAdornment: (
    //         <InputAdornment position="end">
    //           {apiParams.merchant_order_ids.length}
    //         </InputAdornment>
    //       ),
    //     }}
    //   />,
    // ],
  ]);

  return (
    <>
      <LoadingDialog forceOpen={listRes.isLoading || isCreateRequestLoading} />
      <CreateRequestDialog
        isOpen={isCreateRequestDialogOpen}
        onClose={onCreateRequestDialogClose}
        onConfirm={onCreateRequestConfirm}
        content={
          <CreateRequestDialogContent
            fields={createRequestDialogFields}
            setFields={setCreateRequestDialogFields}
          />
        }
      />
      <Box sx={customSx.filterCmdBar}>
        <GeneralBtn
          label="export"
          isHidden={
            !hasPermission(
              FeatureCode.FeaturesWalletManagement.WalletDeploymentRequests
                .Export,
            )
          }
          onClick={onExport}
        />
        <GeneralBtn
          label="create_request"
          isHidden={
            !hasPermission(
              FeatureCode.FeaturesWalletManagement.WalletDeploymentRequests
                .CreateRequest,
            )
          }
          onClick={() => setIsCreateRequestDialogOpen(true)}
          variant={"outlined"}
        />
      </Box>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.created_date}
        {F.last_modified_time}
        {F.chain_name}
        {/* {F.asset_name} */}
        {F.batch_id}
        {F.owner_address}
        {F.contract_address}
        {F.status}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config: {
    t: any;
    te: any;
    omitKeys?: string[];
    getOneWalletVersionInfo: any;
  },
): any[][] => {
  const { t, te, omitKeys = [], getOneWalletVersionInfo } = config;
  const res = array.map((item: any) => {
    const decimal = getDecimal(item);
    const minerFeeDecimal = getDecimal({
      chain_type: item.batch.chain_type,
      chain_id: item.batch.chain_id,
      asset_name: item.batch.miner_fee_asset_name,
    });

    const walletVersionDescription =
      getOneWalletVersionInfo({
        chainType: item.chain_type,
        chainId: item.chain_id,
        walletVersion: String(item.contract_wallet_version),
      })?.description || "";

    const toAmount = (amount: number | string, dec: string = decimal) =>
      displayAmount(amount, dec);

    const mappedResult = [
      ["batch_id", t("batch_id"), item.batch_id],
      [
        "chain_name",
        t("chain_name"),
        findChainInfo(item.chain_type, item.chain_id)?.name,
      ],
      [
        "auto_rent_resource",
        t("auto_rent_resource"),
        isOperationSmartAccount(item.batch.smart_account_type)
          ? te("yes")
          : te("no"),
      ],
      ["owner_address", t("owner_address"), item.owner_address],
      // [
      //   "owner_wallet_type",
      //   t("owner_wallet_type"),
      //   te(WalletType[item.owner_wallet_type]),
      // ],
      ["contract_address", t("contract_address"), item.contract_address],
      [
        "contract_wallet_name",
        t("contract_wallet_name"),
        item.contract_wallet_name,
      ],
      // [
      //   "contract_wallet_type",
      //   t("contract_wallet_type"),
      //   te(WalletType[item.contract_wallet_type]),
      // ],
      [
        "contract_wallet_version",
        t("contract_wallet_version"),
        item.contract_wallet_version,
      ],
      [
        "contract_wallet_version_description",
        t("contract_wallet_version_description"),
        walletVersionDescription,
      ],
      [
        "contract_address_verified",
        t("contract_address_verified"),
        item.contract_address_verified ? t("yes") : t("no"),
      ],

      ["status", t("status"), te(Enum.operationBatchStatus[item.batch.status])],
      ["created_by", t("created_by"), item?.batch?.created_by],
      [
        "created_date",
        t("created_date"),
        toDisplayTime(item?.batch?.created_date),
      ],
      [
        "last_modified_date",
        t("last_modified_date"),
        toDisplayTime(item?.batch?.last_modified_date),
      ],
      ["rawData", "", item],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });
  const output = res.map(listMappingTransform(which));
  return output;
};
